import styled from "@emotion/styled";
import { Link } from "@tanstack/react-router";
import { growFromBottomRounded, pulseOpacity } from "styles/animations";
import { Flex, Text } from "styles/utility-components";

import { currencyFormatLong, numberFormatLong } from "components/app_layout/Typography";
import { Stage } from "components/shared/stage/Stage";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { EventNames, useTracking } from "lib/tracking";
import { PipelineTab, StageItem } from "./types";

type PipelineStageProps = {
  item: StageItem;
  highestCount: number;
  isLoading: boolean;
  linkUrl: string;
  trackingEventName: EventNames;
  pipelineTab: PipelineTab;
  value: number;
};

export function PipelineStage({
  item,
  highestCount,
  isLoading,
  linkUrl,
  trackingEventName,
  pipelineTab,
  value,
}: PipelineStageProps) {
  const { logEvent } = useTracking();

  const handleClick = () =>
    logEvent(trackingEventName, {
      "Context source": "Homepage pipeline",
      "Page source": "Homepage",
      "Pipeline tab": pipelineTab === "leads" ? "Leads" : "Bids",
      "Stage group": item.group,
      "Stage name": item.text,
      "Stage index": item.order,
    });

  const content = (
    <>
      <TooltipComponent
        content={
          !isLoading && value > 0 ? (
            <Flex column gap="4px">
              <Text white fontWeight="500" description>
                Value in {item.text}
              </Text>
              <Text white bold description>
                {currencyFormatLong(value, "GBP")}
              </Text>
            </Flex>
          ) : undefined
        }
        side="top"
        delayDuration={200}
      >
        <ChartContainer>
          {isLoading ? (
            <LoadingChart value={item.count} maxValue={highestCount} />
          ) : (
            <Chart value={item.count} maxValue={highestCount} color={item.iconColor} />
          )}
        </ChartContainer>
      </TooltipComponent>
      {isLoading ? (
        <SkeletonText width="50px" fontSize={17} />
      ) : (
        <CountText bold>{numberFormatLong(item.count)}</CountText>
      )}
      <StageLabelContainer>
        {isLoading && !item.text ? (
          <SkeletonText width="100px" fontSize={17} />
        ) : (
          <Stage
            label={item.text}
            iconName={item.iconName}
            color={item.color}
            iconColor={item.iconColor}
          />
        )}
      </StageLabelContainer>
    </>
  );

  if (!isLoading) {
    return (
      <StageBoxLink to={linkUrl} onClick={handleClick}>
        {content}
      </StageBoxLink>
    );
  }

  return (
    <StageBox href="#" onClick={handleClick}>
      {content}
    </StageBox>
  );
}

const stageBoxStyles = (theme: { colors: { sysBackgroundAlternative: string } }) =>
  ({
    display: "flex",
    flexDirection: "column",
    padding: "8px",
    gap: "16px",
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: "140px",
    "&:hover": {
      backgroundColor: theme.colors.sysBackgroundAlternative,
      borderRadius: "4px",
    },
  }) as const;

const StageBox = styled.a(({ theme }) => stageBoxStyles(theme));

const StageBoxLink = styled(Link)(({ theme }) => stageBoxStyles(theme));

const ChartContainer = styled.div({
  height: "180px",
  display: "flex",
  alignItems: "flex-end",
  width: "100%",
});

const Chart = styled.div<{ value: number; maxValue: number; color?: string }>(
  ({ value, maxValue, color, theme }) => ({
    height: `${(Math.min(value, maxValue) / (maxValue || 1)) * 100}%`,
    minHeight: value > 0 ? "16px" : "0",
    width: "100%",
    backgroundColor: color ?? theme.colors.sysPrimaryDefault,
    borderRadius: "4px",
    animation: `${growFromBottomRounded} 1s ease-in-out`,
  }),
);

const LoadingChart = styled.div<{ value: number; maxValue: number }>(
  ({ value, maxValue, theme }) => ({
    height: `${(Math.min(value, maxValue) / (maxValue || 1)) * 100}%`,
    minHeight: value > 0 ? "16px" : "0",
    width: "100%",
    backgroundColor: theme.colors.sysBorderDecorative,
    borderRadius: "4px",
    animation: `${growFromBottomRounded} 0.6s ease-out, ${pulseOpacity} 1.5s ease-in-out infinite`,
  }),
);

const CountText = styled(Text)({
  fontSize: "17px",
});

const StageLabelContainer = styled.div({
  height: "30px",
  overflow: "hidden",
  width: "100%",
  // Override Stage component's fit-content to allow truncation
  "& > *": {
    width: "100%",
    maxWidth: "100%",
  },
});
